import axios from '@/axios';

/**
 * API 호출 전에 파라미터를 정리하는 함수
 * 빈 문자열이나 유효하지 않은 값을 제거
 */
const sanitizeParams = (params) => {
    const sanitized = { ...params };
    
    // 키워드 검색을 위해 원본 값 저장
    const keyword = params.keyword || '';
    
    // 각 파라미터에 대해 검사
    Object.keys(sanitized).forEach(key => {
        const value = sanitized[key];
        
        // 빈 문자열이거나 undefined, null인 경우 제거
        if (value === '' || value === undefined || value === null) {
            delete sanitized[key];
            return;
        }
        
        // keyword는 백엔드에서 지원하지 않으므로 제거
        if (key === 'keyword') {
            delete sanitized[key];
            return;
        }
        
        // treatmentId를 백엔드 파라미터인 treatmentGroupId로 변환
        if (key === 'treatmentId') {
            const num = Number(value);
            if (isNaN(num)) {
                delete sanitized[key];
            } else {
                // treatmentId를 treatmentGroupId로 변환
                sanitized['treatmentGroupId'] = num;
                delete sanitized[key]; // 원래 treatmentId 삭제
            }
        }
    });
    
    // 키워드 검색을 위해 원본 값 반환
    sanitized._keyword = keyword;
    
    return sanitized;
};

/**
 * API 응답 데이터 정규화 함수
 * 다양한 API 응답 형식을 일관된 구조로 변환
 */
const normalizeResponse = (response) => {
    console.log('API 원본 응답:', response);
    
    // 응답 데이터가 없는 경우
    if (!response) {
        return { items: [], total: 0 };
    }
    
    // datascrape 백엔드 응답 형식: { data: [], total: number, page: number, limit: number }
    if (response.data && Array.isArray(response.data) && response.total !== undefined) {
        console.log('백엔드 표준 응답 구조 감지: { data: [], total: number }');
        return { 
            items: response.data, 
            total: response.total,
            page: response.page,
            limit: response.limit
        };
    }
    
    // 응답이 이미 items와 total 구조를 가진 경우
    if (response.items !== undefined && response.total !== undefined) {
        return response;
    }
    
    // data 내부에 items와 total이 있는 경우
    if (response.data && response.data.items !== undefined && response.data.total !== undefined) {
        return response.data;
    }
    
    // data 자체가 배열인 경우
    if (Array.isArray(response.data)) {
        return { items: response.data, total: response.data.length };
    }
    
    // response 자체가 배열인 경우
    if (Array.isArray(response)) {
        return { items: response, total: response.length };
    }
    
    // data 내부의 result가 배열인 경우
    if (response.data && Array.isArray(response.data.result)) {
        return { items: response.data.result, total: response.data.total || response.data.result.length };
    }
    
    // 기타 예상치 못한 구조인 경우 최선의 추측
    return { 
        items: response.data || response.items || response.result || [], 
        total: response.total || response.count || 0 
    };
};

/**
 * 백엔드 반환 데이터에서 키워드 검색 필터링 실행
 * @param {Array} items - 백엔드에서 반환된 아이템 배열
 * @param {string} keyword - 검색 키워드
 * @returns {Array} - 필터링된 아이템 배열
 */
const filterByKeyword = (items, keyword) => {
    if (!keyword || keyword.trim() === '') {
        return items;
    }
    
    const lowerKeyword = keyword.toLowerCase();
    
    return items.filter(item => {
        // 병원명에서 검색
        if (item.hospital_name && item.hospital_name.toLowerCase().includes(lowerKeyword)) {
            return true;
        }
        
        // 이름에서 검색
        if (item.name && item.name.toLowerCase().includes(lowerKeyword)) {
            return true;
        }
        
        // 주소에서 검색
        if (item.address && item.address.toLowerCase().includes(lowerKeyword)) {
            return true;
        }
        
        // 진료과목에서 검색
        if (item.department && item.department.toLowerCase().includes(lowerKeyword)) {
            return true;
        }
        
        // 카테고리에서 검색
        if (item.category && item.category.toLowerCase().includes(lowerKeyword)) {
            return true;
        }
        
        return false;
    });
};

export const HospitalCrawlingApi = {
    /**
     * 크롤링된 병원 정보 목록 조회
     * @param {Object} params - 검색 파라미터
     * @param {number} [params.page=1] - 페이지 번호
     * @param {number} [params.limit=10] - 페이지당 항목 수
     * @param {string} [params.source] - 크롤링 소스 (ex: modoodoc, docple 등)
     * @param {string} [params.city] - 지역 (ex: 서울, 경기도 등)
     * @param {number} [params.treatmentId] - 치료 그룹 ID (백엔드에서는 treatmentGroupId로 처리됨)
     * @param {string} [params.keyword] - 검색어 (병원명, 주소 등) - 프론트엔드에서 필터링
     * @returns {Promise<{items: Array, total: number}>}
     */
    getHospitals: async (params = {}) => {
        try {
            // URL 직접 구성
            let url = '/datascrapes/hospitals';
            
            // 파라미터 정리
            const sanitizedParams = sanitizeParams(params);
            console.log('요청 파라미터:', sanitizedParams);
            
            // 키워드 검색을 위해 원본 값 저장
            const keyword = sanitizedParams._keyword || '';
            delete sanitizedParams._keyword;
            
            // URL 파라미터로 변환
            const queryParams = new URLSearchParams();
            
            // 기본값 설정
            queryParams.append('page', sanitizedParams.page || '1');
            
            // 페이지당 항목 수 - limit 값 증가 (백엔드 응답을 모두 표시하기 위해)
            // 키워드 검색이 있는 경우 모든 데이터를 가져와서 프론트엔드에서 필터링
            const originalLimit = sanitizedParams.limit || 10;
            const effectiveLimit = keyword ? 100 : originalLimit; // 키워드 검색 시 더 많은 데이터 가져오기
            queryParams.append('limit', effectiveLimit);
            
            // 추가 파라미터
            if (sanitizedParams.source) {
                queryParams.append('source', sanitizedParams.source);
            }
            
            if (sanitizedParams.city) {
                queryParams.append('city', sanitizedParams.city);
            }
            
            // treatmentGroupId는 유효한 숫자일 경우에만 추가
            if (sanitizedParams.treatmentGroupId && !isNaN(Number(sanitizedParams.treatmentGroupId))) {
                queryParams.append('treatmentGroupId', sanitizedParams.treatmentGroupId);
            }
            
            // 파라미터가 있으면 URL에 추가
            const queryString = queryParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
            
            console.log("API 요청 URL:", url);
            
            const response = await axios.get(url);
            console.log("API 응답 데이터:", response.data);
            
            // 테스트 목적 - 데이터가 없는 경우 더미 데이터 반환
            if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
                console.log("API 응답이 비어있습니다. 더미 데이터를 사용합니다.");
                return {
                    items: [
                        {
                            _id: 'dummy1',
                            hospital_name: '더미 병원 1',
                            address: '서울 강남구 역삼동 123',
                            department: '치과',
                            category: '치과의원',
                            score: 4.5,
                            review_count: 10,
                            image_url: '',
                            treatments: [
                                {
                                    _id: 't1',
                                    treatment_groups_id: 10,
                                    treatment_groups_name: '임플란트(단일)',
                                    min_normal_price: 1000000,
                                    max_normal_price: 1200000,
                                    min_event_price: 800000,
                                    max_event_price: 1000000,
                                    view_count: 150,
                                    submit_count: 20
                                }
                            ]
                        },
                        {
                            _id: 'dummy2',
                            hospital_name: '더미 병원 2',
                            address: '서울 서초구 방배동 456',
                            department: '성형외과',
                            category: '전문의원',
                            score: 4.0,
                            review_count: 8,
                            image_url: '',
                            treatments: []
                        }
                    ],
                    total: 2
                };
            }
            
            // 응답 데이터 정규화
            const normalizedResponse = normalizeResponse(response.data);
            
            // 키워드가 있는 경우 프론트엔드에서 필터링
            if (keyword && keyword.trim() !== '') {
                console.log(`클라이언트 측에서 '${keyword}' 키워드로 필터링 중...`);
                const filteredItems = filterByKeyword(normalizedResponse.items, keyword);
                
                // 원래 limit과 page 값 고려하여 페이징 처리
                const startIndex = (Number(sanitizedParams.page) - 1) * originalLimit;
                const endIndex = startIndex + originalLimit;
                
                // 필터링된 아이템 중 현재 페이지에 표시할 항목만 선택
                const paginatedItems = filteredItems.slice(startIndex, endIndex);
                
                return {
                    items: paginatedItems,
                    total: filteredItems.length, // 필터링 된 전체 개수
                    filteredTotal: filteredItems.length,
                    originalLimit: originalLimit,
                    startIndex: startIndex,
                    endIndex: endIndex
                };
            }
            
            return normalizedResponse;
        } catch (error) {
            console.error('API 요청 오류:', error);
            throw error;
        }
    },

    /**
     * 크롤링된 병원 상세 정보 조회 (선택적, 향후 구현 시 사용)
     * @param {string} hospitalId - 병원 ID
     * @returns {Promise<Object>}
     */
    getHospitalDetail: async (hospitalId) => {
        console.log("병원 상세 정보 조회 요청:", { hospitalId });
        const response = await axios.get(`/datascrapes/hospitals/${hospitalId}`);
        return response.data;
    },

    /**
     * 병원 정보 DB 동기화 (선택적, 향후 구현 시 사용)
     * @param {string} hospitalId - 병원 ID
     * @param {Object} syncData - 동기화 데이터
     * @returns {Promise<Object>}
     */
    syncHospitalData: async (hospitalId, syncData = {}) => {
        console.log("병원 정보 동기화 요청:", { hospitalId, syncData });
        const response = await axios.post(`/datascrapes/hospitals/${hospitalId}/sync`, syncData);
        return response.data;
    }
};
